import React, {FC} from "react"

import authentication from "@irati/auth"
import GenericModal from "@irati/ui/src/GenericModal"
import Flex from "@irati/ui/src/Flex"
import {H1, H3, Body} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {
  BankName,
  BankAccountNumber,
  BankSortCode,
  BankNameAUD,
  BankAccountNumberAUD,
  BSBAUD
} from "@irati/wallet/constants/bankTransferDetails"
import {PaymentCurrencyType, Topup} from "@irati/types"
import {StyledButton} from "../../Tenant/WalletPopups/styled"
import {TopUpPayload} from "../../../containers/AdminReward/AdminRewardTopUp"

interface Props extends InjectIntl {
  showConfirmationModal: boolean,
  poNumber: string | null,
  goBackToTransactions: () => void,
  totalToPay: number,
  payload: TopUpPayload,
  rewardWalletTopup: Topup
}

const BankTransferConfirmationModal: FC<Props> = ({
  showConfirmationModal,
  poNumber,
  goBackToTransactions,
  totalToPay,
  payload,
  rewardWalletTopup,
  intl
}) => {

  const date = intl.formatDate(rewardWalletTopup?.metadata?.createdAt, {
    day: "numeric",
    month: "short",
    year: "numeric"
  })

  const isGBPCurrency = payload.purchaseCurrency === PaymentCurrencyType.Gbp

  return (
    <GenericModal
      hideActions
      show={showConfirmationModal}
      onHide={goBackToTransactions}
      onExited={goBackToTransactions}>
      <Flex direction="column" alignItems="center" marginTop={4} marginBottom={4}>
        <H1 marginBottom={2}>
          <FormattedMessage id="wallet.reward.checkout.bankTransfer.confirmationModal.title" />
        </H1>
        <Flex alignItems="center">
          <Icon icon={IconTypes.rewardPoints} size={28} />
          <H3 marginLeft={0.5}>{payload.rpPoints} <FormattedMessage id="wallet.reward.admin.confirmationModal.points" /></H3>
        </Flex>
      </Flex>
      <Body marginBottom={3} color={theme.colors.greys.black}>
        <FormattedMessage id="wallet.reward.checkout.bankTransfer.confirmationModal.body1" />
      </Body>
      <Body marginBottom={3} color={theme.colors.greys.black}>
        <FormattedMessage id="wallet.reward.checkout.bankTransfer.confirmationModal.body2" values={{email: authentication.user().email}} />
      </Body>
      <Body marginBottom={3} color={theme.colors.greys.black}>
        <FormattedMessage id="wallet.reward.checkout.bankTransfer.confirmationModal.body3" />
      </Body>
      <Card bgColor={theme.colors.greys.lightest}>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage id="wallet.reward.checkout.bankTransfer.confirmationModal.rewardPointsRequested" />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{payload.rpPoints}</Body>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage id="wallet.reward.admin.card.topup.amountDue" />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{totalToPay} {payload.purchaseCurrency}</Body>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Flex flex="1">
            <Body>
              <FormattedMessage id="wallet.tenant.topUp.poNumber" />
            </Body>
          </Flex>
          <Flex flex="1" justifyContent="flex-end">
            <Body color={theme.colors.greys.black} weight="bold" wordBreak="break-all">{poNumber}</Body>
          </Flex>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage id="wallet.tenant.topUp.invoicePopUp.dueDate" />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{date}</Body>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage id="wallet.tenant.topUp.invoicePopUp.bank" />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{!isGBPCurrency ? BankNameAUD : BankName}</Body>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage id="wallet.tenant.topUp.invoicePopUp.bankAccountNumber" />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{!isGBPCurrency ? BankAccountNumberAUD : BankAccountNumber}</Body>
        </Flex>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body>
            <FormattedMessage
              id={!isGBPCurrency ? "wallet.tenant.topUp.invoicePopUp.bsb" : "wallet.tenant.topUp.invoicePopUp.bankSortCode"} />
          </Body>
          <Body color={theme.colors.greys.black} weight="bold">{!isGBPCurrency ? BSBAUD : BankSortCode}</Body>
        </Flex>
      </Card>
      <StyledButton primary large full onClick={goBackToTransactions}>
        <FormattedMessage id="wallet.reward.admin.confirmationModal.btn.backToRewardWallet" />
      </StyledButton>
    </GenericModal>
  )
}

export default injectIntl(BankTransferConfirmationModal)
